import { buildUrl, usKidsGolfService } from '@/libs/api/instances/us-kids-golf-instance';
import type { RequestOptions } from '@/libs/api/types';

import type { FamilyActivity } from './get-family-activities';

/** Path (relative to `buildUrl`) for a co-guardian's activities on a specific player. */
export const playerCoGuardianActivitiesEndpoint = (playerId: string, guardianUid: string) =>
  `/guardians/me/players/${playerId}/guardians/${guardianUid}/activities`;

/**
 * Query params for the co-guardian activity feed (mirrors the family-activities query).
 */
export type CoGuardianActivitiesParams = {
  /** Page size when `paginate=true` (1–50, ignored otherwise). */
  limit?: number;
  /** Opaque cursor from a previous page; omit for the first page. */
  cursor?: string;
  /** When true, page the results; default (false) returns all activities. */
  paginate?: boolean;
};

/** Page of a co-guardian's activity feed; item shape mirrors {@link FamilyActivity}. */
export type CoGuardianActivitiesPage = {
  activities: FamilyActivity[];
  /** Cursor for the next page; null on the last page. */
  nextCursor: string | null;
};

/**
 * Fetches the recent activities of both guardians that relate to a specific player (BR-01)
 * via GET `/api/v1/guardians/me/players/{playerId}/guardians/{guardianUid}/activities`
 * (newest first). Authenticated with the Firebase bearer token.
 *
 * The endpoint scopes activities to the player navigated from, so the client renders the
 * response as-is (no extra kid filtering needed).
 */
export async function fetchCoGuardianActivities(
  playerId: string,
  guardianUid: string,
  params?: CoGuardianActivitiesParams,
  options?: RequestOptions,
): Promise<CoGuardianActivitiesPage> {
  return await usKidsGolfService.get<CoGuardianActivitiesPage>(
    buildUrl(playerCoGuardianActivitiesEndpoint(playerId, guardianUid)),
    params,
    options,
  );
}
